"use client";

import React, {useEffect, useState} from "react";
import {useRouter} from "next/navigation";
import PageWrapper from "../components/ui/PageWrapper";
import EventsSection from "../components/ui/EventsSection";
import {TopCommentatorsList} from "../components/layout/CommentatorsList";
import WiderTalkHeading from "../components/ui/WiderTalkHeading";
import FeaturedRecordings from "../components/FeaturedRecordings";
import {useGlobalContext} from "../context";
import TourSignupModal from "../components/modals/TourSignupModal";
import MailingListModal from "@/app/components/modals/MailingListModal";
import CommentatorDetailsModal from "../components/modals/CommentatorDetailsModal";
import {Commentator} from "../types";
import {Recording} from "../service/playbackService";
import {PlaybackService} from "../service/playbackService";

export default function Page() {
    const {userType, userData} = useGlobalContext();
    const router = useRouter();
    const [isTourSignupModalOpen, setIsTourSignupModalOpen] = useState(false);
    const [isWhatIsBannerExpanded, setIsWhatIsBannerExpanded] = useState(false);
    const [isHowDoesItWorkExpanded, setIsHowDoesItWorkExpanded] = useState(false);
    const [isMailingListOpen, setIsMailingListOpen] = useState(false);

    // Commentator modal state
    const [selectedCommentator, setSelectedCommentator] = useState<Commentator | null>(null);
    const [selectedRank, setSelectedRank] = useState<number | null>(null);
    const [isCommentatorModalOpen, setIsCommentatorModalOpen] = useState(false);

    // Featured recordings state
    const [featuredRecordings, setFeaturedRecordings] = useState<Recording[]>([]);
    const [recordingsLoading, setRecordingsLoading] = useState(true);

    useEffect(() => {
        if (userType === "commentator") {
            router.push("/commentate");
        }
    }, [userType, router]);

    // Fetch featured recordings
    useEffect(() => {
        const fetchFeaturedRecordings = async () => {
            try {
                setRecordingsLoading(true);
                const response = await PlaybackService.getRecordings();
                // Filter for featured and visible recordings
                const featured = response.recordings.filter(
                    recording => recording.isFeatured && recording.isVisible
                );
                setFeaturedRecordings(featured);
            } catch (error) {
                console.error('Error fetching featured recordings:', error);
            } finally {
                setRecordingsLoading(false);
            }
        };

        fetchFeaturedRecordings();
    }, []);

    // Show mailing-list modal on first visit
    useEffect(() => {
        try {
            const flag = typeof window !== 'undefined' ? localStorage.getItem('wt_ml_prompted') : '1';
            if (!flag) {
                const t = setTimeout(() => setIsMailingListOpen(true), 1200);
                return () => clearTimeout(t);
            }
        } catch (e) {
            // ignore
        }
    }, []);

    const handleCommentatorClick = (commentator: Commentator, rank: number) => {
        setSelectedCommentator(commentator);
        setSelectedRank(rank);
        setIsCommentatorModalOpen(true);
    };

    const handleCloseCommentatorModal = () => {
        setIsCommentatorModalOpen(false);
        setSelectedCommentator(null);
        setSelectedRank(null);
    };

    return (
        <>
            <MailingListModal
                isOpen={isMailingListOpen}
                onClose={() => {
                    try { localStorage.setItem('wt_ml_prompted', '1'); } catch {}
                    setIsMailingListOpen(false);
                }}
            />
            {/* Hero Section - Text-based with background image */}
            <section className="relative w-full mb-8">
                <div className="relative bg-stone-900 overflow-hidden">
                    <img
                        src="https://widertalkstack-profilepicturebucket13af4749-e94bwazlhgzc.s3.eu-west-1.amazonaws.com/poliasi2.jpg"
                        alt="Sports event"
                        className="w-full h-[200px] md:h-[250px] lg:h-[300px] object-cover"
                    />

                    {/* Overlay gradient */}
                    <div className="absolute inset-0 bg-gradient-to-br from-widertalk-navy/90 via-widertalk-navy/80 to-stone-900/90"></div>

                    {/* Hero content */}
                    <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-full max-w-7xl mx-auto px-4 lg:px-8">
                            <div className="max-w-3xl mx-auto text-center">
                                <h1 className="text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-light text-white tracking-tight leading-tight mb-3 md:mb-4"
                                    style={{fontFamily: 'Gendy, sans-serif'}}>
                                    Sports Commentary, Your Way
                                </h1>
                                <p className="text-sm md:text-base lg:text-lg text-stone-200 font-light leading-relaxed max-w-2xl mx-auto">
                                    Watch live events with multiple expert commentators. Choose who you want to hear, or become a commentator yourself.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* What is WiderTalk Banner - Collapsible section */}
            <section className="w-full mb-8">
                <div className="bg-gradient-to-r from-widertalk-navy to-widertalk-navy/90 text-white py-4 px-6">
                    <div className="max-w-7xl mx-auto">
                        {/* Clickable header to toggle expansion */}
                        <button
                            onClick={() => setIsWhatIsBannerExpanded(!isWhatIsBannerExpanded)}
                            className="w-full flex items-center justify-between group focus:outline-none focus:ring-2 focus:ring-widertalk-lime focus:ring-offset-2 focus:ring-offset-widertalk-navy rounded-lg p-2 -m-2"
                        >
                            <div className="flex items-center space-x-3">
                                <div className="bg-widertalk-lime/20 rounded-full p-2">
                                    <svg className="w-5 h-5 text-widertalk-lime" fill="none" stroke="currentColor"
                                         viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                              d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                                    </svg>
                                </div>
                                <div className="text-left">
                                    <h3 className="text-lg font-semibold text-white group-hover:text-widertalk-lime transition-colors text-left"
                                        style={{fontFamily: 'Gendy, sans-serif'}}>
                                        What is WiderTalk?
                                    </h3>
                                    <p className="text-sm text-white/80 text-left">Learn about our sports commentary
                                        platform</p>
                                </div>
                            </div>
                            <div className="flex items-center space-x-2">
                                <span className="text-sm text-white/70 group-hover:text-white transition-colors">
                                    {isWhatIsBannerExpanded ? 'Collapse' : 'Learn more'}
                                </span>
                                <svg
                                    className={`w-4 h-4 text-white/70 group-hover:text-white transition-all duration-200 ${isWhatIsBannerExpanded ? 'rotate-180' : 'rotate-0'}`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                          d="M9 5l7 7-7 7"/>
                                </svg>
                            </div>
                        </button>

                        {/* Collapsible content */}
                        <div
                            className={`transition-all duration-300 ease-in-out overflow-hidden ${isWhatIsBannerExpanded ? 'max-h-96 opacity-100 mt-4' : 'max-h-0 opacity-0'}`}>
                            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                                <div className="space-y-2">
                                    <h4 className="font-semibold text-widertalk-lime"
                                        style={{fontFamily: 'Gendy, sans-serif'}}>Watch & Listen</h4>
                                    <p className="text-sm text-widertalk-cream/90">
                                        Stream live events with multiple expert commentators. Choose who you want to
                                        hear.
                                    </p>
                                </div>

                                <div className="space-y-2">
                                    <h4 className="font-semibold text-widertalk-lime"
                                        style={{fontFamily: 'Gendy, sans-serif'}}>Become a Commentator</h4>
                                    <p className="text-sm text-widertalk-cream/90">
                                        Share your passion by providing live commentary. Build your audience.
                                    </p>
                                </div>

                                <div className="space-y-2 md:col-span-2 lg:col-span-1">
                                    <h4 className="font-semibold text-widertalk-lime"
                                        style={{fontFamily: 'Gendy, sans-serif'}}>Community First</h4>
                                    <p className="text-sm text-widertalk-cream/90">
                                        Democratizing sports commentary - giving everyone a voice and choice.
                                    </p>
                                </div>
                            </div>

                            <div className="mt-6 pt-4 border-t border-widertalk-lime/20">
                                <p className="text-sm text-widertalk-cream/80 text-center lg:text-center mb-3">
                                    Join thousands of sports fans in the WiderTalk community.
                                    <span className="font-medium text-widertalk-lime"> Experience sports like never before.</span>
                                </p>
                                <p className="text-xs text-widertalk-cream/60 text-center lg:text-left">
                                    <a href="/faq" className="hover:text-widertalk-lime transition-colors underline">
                                        *See more in FAQ
                                    </a>
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* How does it work Banner - Collapsible section matching "What is WiderTalk" style */}
            <section className="w-full mb-8">
                <div
                    className="bg-gradient-to-r from-widertalk-lime to-widertalk-lime/90 text-widertalk-navy py-4 px-6">
                    <div className="max-w-7xl mx-auto">
                        {/* Clickable header to toggle expansion */}
                        <button
                            onClick={() => setIsHowDoesItWorkExpanded(!isHowDoesItWorkExpanded)}
                            className="w-full flex items-center justify-between group focus:outline-none focus:ring-2 focus:ring-widertalk-navy focus:ring-offset-2 focus:ring-offset-widertalk-lime rounded-lg p-2 -m-2"
                        >
                            <div className="flex items-center space-x-3">
                                <div className="bg-widertalk-navy/20 rounded-full p-2">
                                    <svg className="w-5 h-5 text-widertalk-navy" fill="none" stroke="currentColor"
                                         viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                              d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                                    </svg>
                                </div>
                                <div className="text-left">
                                    <h3 className="text-lg font-semibold text-widertalk-navy group-hover:text-widertalk-navy/80 transition-colors text-left"
                                        style={{fontFamily: 'Gendy, sans-serif'}}>
                                        How does it work?
                                    </h3>
                                    <p className="text-sm text-widertalk-navy/80 text-left">New to WiderTalk? Get
                                        started with our quick guide</p>
                                </div>
                            </div>
                            <div className="flex items-center space-x-2">
                                <span
                                    className="text-sm text-widertalk-navy/70 group-hover:text-widertalk-navy transition-colors">
                                    {isHowDoesItWorkExpanded ? 'Collapse' : 'Learn more'}
                                </span>
                                <svg
                                    className={`w-4 h-4 text-widertalk-navy/70 group-hover:text-widertalk-navy transition-all duration-200 ${isHowDoesItWorkExpanded ? 'rotate-180' : 'rotate-0'}`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                          d="M9 5l7 7-7 7"/>
                                </svg>
                            </div>
                        </button>

                        {/* Collapsible content */}
                        <div
                            className={`transition-all duration-300 ease-in-out overflow-hidden ${isHowDoesItWorkExpanded ? 'max-h-96 opacity-100 mt-4' : 'max-h-0 opacity-0'}`}>
                            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                                <div className="space-y-2">
                                    <h4 className="font-semibold text-widertalk-navy"
                                        style={{fontFamily: 'Gendy, sans-serif'}}>1. Sign Up</h4>
                                    <p className="text-sm text-widertalk-navy/90">
                                        Create your account to access live events and choose your viewing experience.
                                    </p>
                                </div>

                                <div className="space-y-2">
                                    <h4 className="font-semibold text-widertalk-navy"
                                        style={{fontFamily: 'Gendy, sans-serif'}}>2. Choose Your Event</h4>
                                    <p className="text-sm text-widertalk-navy/90">
                                        Browse live and upcoming events. Select what you want to watch.
                                    </p>
                                </div>

                                <div className="space-y-2 md:col-span-2 lg:col-span-1">
                                    <h4 className="font-semibold text-widertalk-navy"
                                        style={{fontFamily: 'Gendy, sans-serif'}}>3. Pick Your Voice</h4>
                                    <p className="text-sm text-widertalk-navy/90">
                                        Select from multiple expert commentators or become one yourself.
                                    </p>
                                </div>
                            </div>

                            <div className="mt-6 pt-4 border-t border-widertalk-navy/20">
                                <p className="text-sm text-widertalk-navy/80 text-center lg:text-center mb-3">
                                    Ready to get started?
                                    <span
                                        className="font-medium text-widertalk-navy"> Follow our step-by-step guide.</span>
                                </p>
                                <div className="flex justify-center">
                                    <button
                                        onClick={() => router.push('/quick-start')}
                                        className="bg-widertalk-navy text-white px-6 py-2 rounded-lg font-medium hover:bg-widertalk-navy/90 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-widertalk-navy focus:ring-offset-2 focus:ring-offset-widertalk-lime flex items-center space-x-2"
                                    >
                                        <span>Quick Start Guide</span>
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                                  d="M9 5l7 7-7 7"/>
                                        </svg>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <PageWrapper maxWidth="7xl">
                {/* Single Top Video Ad */}
                {/*<div className="flex justify-center mb-8">*/}
                {/*    <AdBlock size="rectangle" position="Homepage Header" isVideo={true} />*/}
                {/*</div>*/}

                {/* Main Content Grid */}
                <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
                    {/* Events Section and Featured Recordings - Takes up 2 columns on XL screens */}
                    <div className="xl:col-span-2 space-y-8">
                        <EventsSection
                            title="Choose an event to watch"
                            userType="viewers"
                        />

                        {/* Featured Recordings Section */}
                        <div className="mt-6">
                            {recordingsLoading ? (
                                <div className="flex items-center justify-center py-8">
                                    <div
                                        className="animate-spin rounded-full h-8 w-8 border-b-2 border-widertalk-navy"></div>
                                    <span className="ml-3 text-gray-600">Loading featured recordings...</span>
                                </div>
                            ) : featuredRecordings.length === 0 ? (
                                <div className="text-center py-8">
                                    <p className="text-gray-600">No featured recordings available at the moment.</p>
                                </div>
                            ) : (
                                <FeaturedRecordings
                                    recordings={featuredRecordings}
                                    onRecordingSelect={(recording: Recording) => {
                                        router.push(`/playback?recording=${recording.compositionArn}`);
                                    }}
                                    loadingPlayback={false}
                                />
                            )}
                        </div>
                    </div>

                    <div className="xl:col-span-1">
                        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg p-6 md:p-8 h-full">
                            <WiderTalkHeading>Top Commentators By Viewer Hours</WiderTalkHeading>
                            <div className="mt-6">
                                <TopCommentatorsList onCommentatorClick={handleCommentatorClick}/>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Feature Highlights */}
                {/*<FeatureHighlights />*/}

                {/* Single Bottom Video Ad */}
                {/*<div className="flex justify-center mt-12">*/}
                {/*    <AdBlock size="rectangle" position="Homepage Footer" isVideo={true} />*/}
                {/*</div>*/}
            </PageWrapper>

            {/* Tour Signup Modal - Initially hidden */}
            <TourSignupModal
                isOpen={isTourSignupModalOpen}
                onClose={() => setIsTourSignupModalOpen(false)}
            />

            {/* Commentator Details Modal */}
            <CommentatorDetailsModal
                isOpen={isCommentatorModalOpen}
                onClose={handleCloseCommentatorModal}
                commentator={selectedCommentator}
                rank={selectedRank || undefined}
            />
        </>
    );
}
